import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { message } from 'antd';
import type { UploadFile } from 'antd';
import clsx from 'clsx';

import { submitFeedback } from '@/services/feedbackService';

import { FileUpload, type FileUploadRef } from './FileUpload';
import { IssueTypeCard } from './IssueTypeCard';
import type { FeedbackContextInfo, FeedbackType } from './types';

const MAX_LENGTH = 2000;

// 从URL参数解析截图列表
// 支持格式: fileUrls=url1&fileUrls=url2 或 fileUrls=url1,url2
const parseFileUrls = (searchParams: URLSearchParams): UploadFile[] => {
  const fileUrls = searchParams.getAll('fileUrls');
  const urls: string[] = [];

  fileUrls.forEach((s) => {
    s.split(',').forEach((url) => {
      const trimmed = url.trim();
      if (trimmed) {
        urls.push(trimmed);
      }
    });
  });

  return urls.slice(0, 5).map((url, index) => ({
    uid: `url-${index}-${Date.now()}`,
    name: url.split('/').pop() || `image-${index + 1}`,
    status: 'done' as const,
    url,
  }));
};

// 从URL参数解析上下文信息（参数名与接口保持一致）
const parseContextInfo = (searchParams: URLSearchParams): FeedbackContextInfo => {
  return {
    sessionId: searchParams.get('sessionId') || 'empty',
    chatId: searchParams.get('chatId') || 'empty',
    requestId: searchParams.get('requestId') || 'empty',
    appLogUrl: searchParams.get('appLogUrl') || '',
    chatLogUrl: searchParams.get('chatLogUrl') || '',
    systemInfo: searchParams.get('systemInfo') || '',
    extensionInfo: searchParams.get('extensionInfo') || '',
    traceId: searchParams.get('traceId') || 'empty',
  };
};

const Feedback = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const fileUploadRef = useRef<FileUploadRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 从URL参数获取初始值
  const isFullscreen = searchParams.get('fullscreen') === 'true';
  const initialFeedbackType = searchParams.get('feedbackType') as FeedbackType;
  const initialDescription = searchParams.get('description') || '';

  // 从URL参数获取上下文信息（不需要用户填写）
  const contextInfo = parseContextInfo(searchParams);

  const [feedbackType, setFeedbackType] = useState<FeedbackType>(
    initialFeedbackType === 'bug_report' || initialFeedbackType === 'feature_request'
      ? initialFeedbackType
      : 'bug_report',
  );
  const [description, setDescription] = useState(initialDescription);
  const [fileList, setFileList] = useState<UploadFile[]>(() => parseFileUrls(searchParams));

  // 根据问题类型获取 placeholder
  const getDescriptionPlaceholder = () => {
    if (feedbackType === 'feature_request') {
      return t('feedback.description.placeholder.suggestion');
    }
    return t('feedback.description.placeholder.feedback');
  };

  // 全局粘贴事件处理（任意位置粘贴图片都会自动上传）
  const handlePaste = useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && file.type.startsWith('image/')) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      // 使用 FileUpload 组件的上传方法
      fileUploadRef.current?.uploadFiles(files);
    }
  }, []);

  // 添加全局粘贴事件监听
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('paste', handlePaste);
    return () => {
      container.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  const handleSubmit = async () => {
    // 验证必填项
    if (!feedbackType) {
      message.error(t('feedback.validation.issueTypeRequired'));
      return;
    }
    if (!description.trim()) {
      message.error(t('feedback.validation.descriptionRequired'));
      return;
    }

    // 检查文件上传状态
    const hasUploading = fileList.some((file) => file.status === 'uploading');
    if (hasUploading) {
      message.error(t('feedback.validation.filesUploading'));
      return;
    }

    const hasError = fileList.some((file) => file.status === 'error');
    if (hasError) {
      message.error(t('feedback.validation.filesUploadFailed'));
      return;
    }

    // 提取已上传文件的URL数组
    const fileUrls = fileList
      .filter((file) => file.status === 'done' && file.url)
      .map((file) => file.url as string);

    try {
      const response = await submitFeedback({
        feedbackType,
        description,
        fileUrls: fileUrls.length > 0 ? fileUrls : undefined,
        sessionId: contextInfo.sessionId || undefined,
        chatId: contextInfo.chatId || undefined,
        requestId: contextInfo.requestId || undefined,
        appLogUrl: contextInfo.appLogUrl || undefined,
        chatLogUrl: contextInfo.chatLogUrl || undefined,
        systemInfo: contextInfo.systemInfo || undefined,
        extensionInfo: contextInfo.extensionInfo || undefined,
        traceId: contextInfo.traceId || undefined,
        submitType: 'user',
      });

      if (response?.status === 200) {
        message.success(t('feedback.submitSuccess'));

        // 使用 postMessage 向外层发送完成信息
        const completeMessage = {
          type: 'feedback-submit-complete',
          success: true,
          data: response.data,
        };
        window.parent.postMessage(completeMessage, '*');
        // 同时也发送给 window 自身，以支持非 iframe 场景
        window.postMessage(completeMessage, '*');

        // 如果不在 iframe 中，3秒后自动关闭网页
        if (window.self === window.top) {
          setTimeout(() => {
            window.close();
          }, 1500);
        }
      } else {
        message.error(response?.message || t('feedback.submitFailed'));
      }
    } catch (error) {
      console.error('Feedback submit failed:', error);
      message.error(t('feedback.submitFailed'));
    }
  };

  const handleCancel = () => {
    // 使用 postMessage 向外层发送取消信息
    const cancelMessage = {
      type: 'feedback-submit-cancel',
      success: false,
    };
    window.parent.postMessage(cancelMessage, '*');
    // 同时也发送给 window 自身，以支持非 iframe 场景
    window.postMessage(cancelMessage, '*');

    // 如果不在 iframe 中，直接关闭页面；否则返回上一页
    if (window.self === window.top) {
      window.close();
    }
  };

  return (
    <div
      ref={containerRef}
      className={
        isFullscreen
          ? 'min-w-screen min-h-screen'
          : 'flex min-h-screen items-center justify-center bg-black/50 p-5'
      }
    >
      <div
        className={clsx(
          'flex flex-col items-start border border-[#cccccc26] bg-[#2c2c2d] shadow-[0_8px_40px_rgba(0,0,0,0.3)]',
          isFullscreen ? 'min-w-screen min-h-screen' : 'w-[560px] rounded-lg',
        )}
      >
        {/* Header */}
        {!isFullscreen && (
          <div className="flex h-[52px] w-full items-center justify-between px-4">
            <span className="font-pingfang text-sm font-medium leading-5 text-[#cccccc]">
              {t('feedback.title')}
            </span>
            {/* <CloseIcon
              className="h-4 w-4 cursor-pointer text-[#c5c5c5] transition-colors hover:text-white"
              onClick={handleCancel}
            /> */}
          </div>
        )}

        {/* 问题类型 */}
        <div className="flex w-full items-center gap-1 px-4 pb-2 pt-4">
          <span className="font-pingfang text-xs leading-[18px] text-[#ccccccb2]">
            {t('feedback.issueType.label')}
          </span>
          <span className="font-pingfang text-xs leading-[18px] text-[#f14c4c]">*</span>
        </div>

        <div className="flex w-full gap-3 px-4 pb-4">
          <IssueTypeCard
            type="bug_report"
            selected={feedbackType === 'bug_report'}
            onClick={() => setFeedbackType('bug_report')}
          />
          <IssueTypeCard
            type="feature_request"
            selected={feedbackType === 'feature_request'}
            onClick={() => setFeedbackType('feature_request')}
          />
        </div>

        {/* 问题描述 */}
        <div className="flex w-full items-center gap-1 px-4 pb-2">
          <span className="font-pingfang text-xs leading-[18px] text-[#ccccccb2]">
            {t('feedback.description.label')}
          </span>
          <span className="font-pingfang text-xs leading-[18px] text-[#f14c4c]">*</span>
        </div>

        <div className="w-full px-4 pb-4">
          <div className="relative">
            <textarea
              value={description}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= MAX_LENGTH) {
                  setDescription(value);
                }
              }}
              placeholder={getDescriptionPlaceholder()}
              className="font-pingfang block h-[100px] w-full resize-none rounded border-[0.6px] border-[#cccccc26] bg-[#2c2c2d] p-[7.4px] text-xs leading-[150%] text-[#cccccc] outline-none transition-all placeholder:text-[#cccccc80] hover:bg-[#252526] focus:border-[#007acc] focus:bg-[#2c2c2d]"
              maxLength={MAX_LENGTH}
            />
            <span className="absolute bottom-[4px] right-[6px] text-[10px]">
              <span className="text-[#cccccc]">{description.length}</span>
              <span className="text-[#cccccc80]">/{MAX_LENGTH}</span>
            </span>
          </div>
        </div>

        {/* 截图&录屏 */}
        <div className="flex w-full items-center gap-1 px-4 pb-2">
          <span className="font-pingfang text-xs leading-[18px] text-[#ccccccb2]">
            {t('feedback.screenshots.label')}
          </span>
        </div>

        <div className="w-full px-4 pb-4">
          <FileUpload ref={fileUploadRef} fileList={fileList} onChange={setFileList} />
        </div>

        {/* Footer Buttons */}
        <div className="flex w-full items-center justify-end gap-2 px-3 pb-3">
          <button
            onClick={handleCancel}
            className="font-pingfang h-[22px] rounded bg-[#3a3d41] px-1.5 py-0.5 text-xs font-medium leading-[150%] text-white transition-colors hover:bg-[#4a4d51]"
          >
            {t('feedback.buttons.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="font-pingfang h-[22px] rounded bg-[#0e639c] px-1.5 py-0.5 text-xs font-medium leading-[150%] text-white transition-colors hover:bg-[#1073ac]"
          >
            {t('feedback.buttons.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
